import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import Game from "../game/Game";
import MessageOverlay from "../ui/MessageOverlay";
import Lobby from "./Lobby";
import { cachePlayerEmoji, saveActiveGameSession, clearActiveGameSession } from "../../utils/localStorageUtils";

const GamePage = ({ socket, name, room, setLoggedIn }) => {
  const [start, setStart] = useState(false);
  const [playerCount, setPlayerCount] = useState(0);
  const [playerNames, setPlayerNames] = useState([]); // Add this state
  const [playerColors, setPlayerColors] = useState({});
  const [playerEmojis, setPlayerEmojis] = useState({});
  const [updates, setUpdates] = useState([]);
  const [roundNumber, setRoundNumber] = useState(1);
  const [hasJoinedRoom, setHasJoinedRoom] = useState(false); // Track if player has joined room
  const [snackbars, setSnackbars] = useState([]);
  const nextSnackbarIdRef = useRef(0);
  const [playerReady, setPlayerReady] = useState({}); // Track ready status for each player
  const [isReady, setIsReady] = useState(false); // Current player's ready status
  const [availableColors, setAvailableColors] = useState([]); // Available colors from server
  const [availableEmojis, setAvailableEmojis] = useState([]); // Available emojis from server
  const [countdown, setCountdown] = useState(null); // Game countdown (null when not counting)
  const [chatMessages, setChatMessages] = useState([]); // Chat messages for lobby
  const [firstPlayer, setFirstPlayer] = useState(null); // First player (room host)
  const [botPersonalities, setBotPersonalities] = useState({}); // Bot personalities: { botName: personalityName }
  const [turnTimeRemaining, setTurnTimeRemaining] = useState(null); // Turn timer: seconds remaining
  const [turnTimerPlayer, setTurnTimerPlayer] = useState(null); // Player whose turn it is (for timer)
  const [turnTimerTotal, setTurnTimerTotal] = useState(120); // Total turn time (default, overridden by server)
  const [turnTimerStartTime, setTurnTimerStartTime] = useState(null); // Server timestamp when turn started (for client-side countdown)
  const [disconnectedPlayers, setDisconnectedPlayers] = useState([]); // Array of disconnected player names
  const [gameOptions, setGameOptions] = useState({});
  const disconnectTimeoutRef = useRef(null); // Grace period timer for disconnects
  
  const showMessage = useCallback((message, action = null) => {
    const id = nextSnackbarIdRef.current;
    nextSnackbarIdRef.current += 1;
    setSnackbars(prev => [...prev, { id, message, action }]);
  }, []);
  
  const handleSnackbarClose = (id) => {
    const snackbarToClose = snackbars.find(s => s.id === id);
    setSnackbars(prev => prev.filter(s => s.id !== id));
    
    if (snackbarToClose?.action && typeof snackbarToClose.action === "function") {
      snackbarToClose.action();
    }
  };

  // Set up socket listeners immediately using useLayoutEffect for synchronous setup
  // This ensures listeners are attached before any updates can arrive
  useLayoutEffect(() => {
    if (!socket || !socket.current) return;

    const sc = socket.current;
    
    // Set up listeners first to catch any immediate updates
    const handlePlayerCount = (count) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_count received', { count });
      }
      setPlayerCount(count);
      // Mark as joined when we receive player count
      if (count > 0) {
        setHasJoinedRoom(true);
      }
    };
    
    const handlePlayerNames = (names) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_names received', { count: names?.length });
      }
      setPlayerNames(names);
      // Mark as joined when we receive player names
      if (names && names.length > 0) {
        setHasJoinedRoom(true);
      }
    };

    const handlePlayerNamesWithSlots = (namesWithSlots) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_names_with_slots received', { count: namesWithSlots?.length });
      }
      // Use the array with slots (including undefined) for slot positioning
      if (namesWithSlots && namesWithSlots.length > 0) {
        setPlayerNames(namesWithSlots);
      }
    };
    
    const handlePlayerColors = (colors) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_colors received');
      }
      setPlayerColors(colors || {});
    };
    
    const handlePlayerEmojis = (emojis) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_emojis received');
      }
      setPlayerEmojis(emojis || {});
      
      // Cache the player's own emoji to localStorage
      if (emojis && emojis[name]) {
        cachePlayerEmoji(emojis[name]);
      }
    };

    const handleReadyStatusUpdate = (readyStatus) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] ready_status_update received');
      }
      setPlayerReady(readyStatus || {});
      setIsReady(readyStatus[name] || false); // Update current player's ready status
    };
    
    const handleAvailableAvatarOptions = ({ colors, emojis }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] available_avatar_options received');
      }
      setAvailableColors(colors || []);
      setAvailableEmojis(emojis || []);
    };
    
    const handleFirstPlayer = (firstPlayerName) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] first_player received', { firstPlayerName });
      }
      setFirstPlayer(firstPlayerName);
    };
    
    // Handle start_game event (for reconnecting players)
    const handleStartGame = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] start_game received');
      }
      setCountdown(null); // Clear countdown when game actually starts
      setStart(true);
      
      // Save active game session for auto-reconnect
      saveActiveGameSession(room, name);
    };
    
    // Handle start_variables event (sent when game starts or player reconnects)
    // This is a reliable indicator that the game has started
    const handleStartVariables = () => {
      setStart(true);
    };
    
    sc.on("player_count", handlePlayerCount);
    sc.on("player_names", handlePlayerNames);
    sc.on("player_names_with_slots", handlePlayerNamesWithSlots);
    sc.on("player_colors", handlePlayerColors);
    sc.on("player_emojis", handlePlayerEmojis);
    sc.on("ready_status_update", handleReadyStatusUpdate);
    sc.on("available_avatar_options", handleAvailableAvatarOptions);
    sc.on("first_player", handleFirstPlayer);
    sc.on("start_game", handleStartGame); // Set up immediately for reconnecting players
    sc.on("start_variables", handleStartVariables); // Also listen for start_variables (reconnection indicator)
    
    // Request current room state immediately after listeners are set up
    // This ensures we have the latest player list
    sc.emit("get_room_state", { room });
    
    // Request available avatar options from server
    sc.emit("get_available_avatar_options", { room });
    
    return () => {
      sc.off("player_count", handlePlayerCount);
      sc.off("player_names", handlePlayerNames);
      sc.off("player_names_with_slots", handlePlayerNamesWithSlots);
      sc.off("player_colors", handlePlayerColors);
      sc.off("player_emojis", handlePlayerEmojis);
      sc.off("ready_status_update", handleReadyStatusUpdate);
      sc.off("available_avatar_options", handleAvailableAvatarOptions);
      sc.off("first_player", handleFirstPlayer);
      sc.off("start_game", handleStartGame);
      sc.off("start_variables", handleStartVariables);
    };
  }, [socket, room, name]);
  
  // Separate useEffect for other listeners and handlers
  useEffect(() => {
    if (!socket || !socket.current) return;

    const sc = socket.current;
    
    // start_game listener is now in useLayoutEffect to catch reconnection events immediately

    sc.on("start_game_error", (message) => {
      setCountdown(null); // Clear countdown on error
      showMessage(message);
    });

    sc.on("game_countdown", ({ seconds, message }) => {
      setCountdown(seconds);
    });

    sc.on("game_reset", (message) => {
      setStart(false); // Return to lobby
      setChatMessages([]); // Clear chat messages when game resets
      showMessage(message || "Game has been reset. New players can now join.");
      // Update ready status - will be updated by ready_status_update event
      setIsReady(playerReady[name] || false);
      
      // Clear active game session when game ends
      clearActiveGameSession();
    });

    // Connection lost/disconnect handler - only show if player has joined
    sc.on("disconnect", () => {
      if (hasJoinedRoom) {
        showMessage("🔌❌ Connection to server lost!", () => {
          setLoggedIn(false);
        });
      } else {
        // If not joined yet, just silently return to home
        setLoggedIn(false);
      }
    });

    sc.on("connect_error", () => {
      // Only show error if player has joined
      if (hasJoinedRoom) {
        showMessage("🔌❌ Failed to connect to server!", () => {
          setLoggedIn(false);
        });
      } else {
        // If not joined yet, just silently return to home
        setLoggedIn(false);
      }
    });

    sc.on("add_bot_result", ({ success, message }) => {
      if (!success) {
        showMessage(`❌ ${message}`);
      }
      // Success is handled by player_names update
    });

    sc.on("remove_bot_result", ({ success, message }) => {
      if (!success) {
        showMessage(`❌ ${message}`);
      }
      // Success is handled by player_names update
    });

    sc.on("bot_personalities", (personalities) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] bot_personalities received', personalities);
      }
      setBotPersonalities(personalities || {});
    });

    sc.on("disconnected_players", (disconnectedPlayersArray) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Event] disconnected_players received:', disconnectedPlayersArray);
      }
      setDisconnectedPlayers(disconnectedPlayersArray || []);
    });

    sc.on("game_options_update", (options) => {
      setGameOptions(options || {});
    });

    sc.on("game_option_error", (message) => {
      showMessage(`❌ ${message}`);
    });

    sc.on("change_bot_personality_result", ({ success, message }) => {
      if (!success) {
        showMessage(`❌ ${message}`);
      }
      // Success is handled by bot_personalities update
    });

    sc.on("change_player_color_result", ({ success, message }) => {
      if (!success) {
        showMessage(`❌ ${message}`);
      }
      // Success is handled by player_colors update
    });

    sc.on("change_player_emoji_result", ({ success, message }) => {
      if (!success) {
        showMessage(`❌ ${message}`);
      }
      // Success is handled by player_emojis update
    });

    sc.on("randomize_player_color_result", ({ success, message }) => {
      if (!success) {
        showMessage(`❌ ${message}`);
      }
      // Success is handled by player_colors update
    });

    sc.on("randomize_player_emoji_result", ({ success, message }) => {
      if (!success) {
        showMessage(`❌ ${message}`);
      }
      // Success is handled by player_emojis update
    });

    sc.on("randomize_player_avatar_result", ({ success, message }) => {
      if (!success) {
        showMessage(`❌ ${message}`);
      }
      // Success is handled by player_colors and player_emojis updates
    });

    const handleLobbyChatMessage = (message) => {
      setChatMessages(prev => {
        const newMessages = [...prev, message];
        // Keep only last 100 messages to prevent memory issues
        return newMessages.slice(-100);
      });
    };

    const handleLobbyChatError = (error) => {
      showMessage(`❌ Chat error: ${error}`);
    };

    sc.on("lobby_chat_message", handleLobbyChatMessage);
    sc.on("lobby_chat_error", handleLobbyChatError);

    // Turn timer start event - receive start timestamp for client-side countdown
    const handleTurnTimerStart = ({ playerName, startTime, duration }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] turn_timer_start received', { playerName, startTime, duration });
      }
      setTurnTimerPlayer(playerName);
      setTurnTimerTotal(duration);
      setTurnTimerStartTime(startTime);
      
      // Calculate initial remaining time
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, duration - elapsed);
      setTurnTimeRemaining(remaining);
    };
    
    // Turn timer sync event - periodic server sync to correct client-side drift
    const handleTurnTimerSync = ({ playerName, timeRemaining, totalTime }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] turn_timer_sync received', { playerName, timeRemaining, totalTime });
      }
      // Update with server-corrected value
      setTurnTimeRemaining(timeRemaining);
      setTurnTimerPlayer(playerName);
      setTurnTimerTotal(totalTime);
    };
    
    // Turn timer stop event - clear client-side timer
    const handleTurnTimerStop = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] turn_timer_stop received');
      }
      setTurnTimerStartTime(null);
      setTurnTimeRemaining(null);
      setTurnTimerPlayer(null);
    };
    
    sc.on("turn_timer_start", handleTurnTimerStart);
    sc.on("turn_timer_sync", handleTurnTimerSync);
    sc.on("turn_timer_stop", handleTurnTimerStop);

    return () => {
      // Clear disconnect timeout on cleanup
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
      }
      
      // start_game listener cleanup is in useLayoutEffect
      sc.off("start_game_error");
      sc.off("game_countdown");
      sc.off("game_reset");
      sc.off("disconnect");
      sc.off("connect");
      sc.off("connect_error");
      sc.off("add_bot_result");
      sc.off("change_player_color_result");
      sc.off("change_player_emoji_result");
      sc.off("randomize_player_color_result");
      sc.off("randomize_player_emoji_result");
      sc.off("randomize_player_avatar_result");
      sc.off("bot_personalities");
      sc.off("disconnected_players");
      sc.off("game_options_update");
      sc.off("game_option_error");
      sc.off("change_bot_personality_result");
      sc.off("lobby_chat_message", handleLobbyChatMessage);
      sc.off("lobby_chat_error", handleLobbyChatError);
      sc.off("turn_timer_start", handleTurnTimerStart);
      sc.off("turn_timer_sync", handleTurnTimerSync);
      sc.off("turn_timer_stop", handleTurnTimerStop);
    };
  }, [socket, setLoggedIn, hasJoinedRoom, showMessage, name, playerReady]);

  // Client-side countdown for turn timer (reduces server broadcasts by ~90%)
  useEffect(() => {
    if (!turnTimerStartTime || !turnTimerTotal) {
      // No active timer, clear remaining time
      if (turnTimeRemaining !== null) {
        setTurnTimeRemaining(null);
      }
      return;
    }

    // Calculate remaining time function
    const calculateRemaining = () => {
      const elapsed = Math.floor((Date.now() - turnTimerStartTime) / 1000);
      return Math.max(0, turnTimerTotal - elapsed);
    };

    // Update immediately
    const initialRemaining = calculateRemaining();
    setTurnTimeRemaining(initialRemaining);

    // If already expired, don't start interval
    if (initialRemaining <= 0) {
      setTurnTimeRemaining(0);
      return;
    }

    // Update every second with client-side calculation
    const interval = setInterval(() => {
      const remaining = calculateRemaining();
      setTurnTimeRemaining(remaining);

      // Clear interval when timer expires
      if (remaining <= 0) {
        clearInterval(interval);
        setTurnTimeRemaining(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [turnTimerStartTime, turnTimerTotal]);

  useEffect(() => {
    if (!socket || !socket.current) return;

    const sc = socket.current;
    const handleUpdate = (msg) => {
      setUpdates((updates) => [...updates, msg]);
    };
    const handleRoundUpdate = (round) => {
      setRoundNumber(round); // Update round number when received from server
    };
    
    sc.on("update", handleUpdate);
    sc.on("round_update", handleRoundUpdate);

    return () => {
      sc.off("update", handleUpdate);
      sc.off("round_update", handleRoundUpdate);
    };
  }, [socket]);

  const toggleReady = () => {
    const newReadyStatus = !isReady;
    setIsReady(newReadyStatus);
    socket.current.emit("set_ready", { room, name, ready: newReadyStatus });
  };

  const startGame = () => {
    if (playerCount < 2) {
      showMessage("You need at least 2 players to start the game");
      return;
    }
    
    // Check if all players are ready
    // Filter out undefined/null values (used for slot positioning) before checking ready status
    const validPlayerNames = playerNames.filter(name => name !== undefined && name !== null);
    const allReady = validPlayerNames.length >= 2 && 
                     validPlayerNames.every(playerName => playerReady[playerName] === true);
    
    if (!allReady) {
      showMessage("All players must be ready before starting the game");
      return;
    }
    
    socket.current.emit("start_game", room);
  };

  const leaveRoom = () => {
    socket.current.emit("leave_room", { name, room });
    setLoggedIn(false);
  };

  const changePlayerColor = (color) => {
    socket.current.emit("change_player_color", { room, playerName: name, color });
  };

  const changePlayerEmoji = (emoji) => {
    socket.current.emit("change_player_emoji", { room, playerName: name, emoji });
    // Cache emoji immediately for better UX
    cachePlayerEmoji(emoji);
  };

  const randomizePlayerColor = () => {
    socket.current.emit("randomize_player_color", { room, playerName: name });
  };

  const randomizePlayerEmoji = () => {
    socket.current.emit("randomize_player_emoji", { room, playerName: name });
  };

  const randomizeAll = () => {
    // Use server-side logic to randomize both color and emoji at once
    socket.current.emit("randomize_player_avatar", { room, playerName: name });
  };

  const addBot = (personality = null, slotIndex = null) => {
    if (playerCount >= 8) {
      showMessage("Maximum 8 players allowed");
      return;
    }
    socket.current.emit("add_bot", { room, personality, slotIndex });
  };

  const removeBot = (botName) => {
    socket.current.emit("remove_bot", { room, botName });
  };

  const changeBotPersonality = (botName, personality) => {
    socket.current.emit("change_bot_personality", { room, botName, personality });
  };

  const updateGameOption = (key, value) => {
    socket.current.emit("update_game_option", { room, name, key, value });
  };

  return (
    <div className="gamepage">
      {start ? (
        <Game
          room={room}
          socket={socket}
          name={name}
          setLoggedIn={setLoggedIn}
          roundNumber={roundNumber}
          turnTimeRemaining={turnTimeRemaining}
          turnTimerPlayer={turnTimerPlayer}
          turnTimerTotal={turnTimerTotal}
          disconnectedPlayers={disconnectedPlayers}
          isHost={firstPlayer === name}
        />
      ) : (
        <Lobby
          room={room}
          name={name}
          playerCount={playerCount}
          playerNames={playerNames}
          playerColors={playerColors}
          playerEmojis={playerEmojis}
          updates={updates}
          playerReady={playerReady}
          isReady={isReady}
          onToggleReady={toggleReady}
          onStartGame={startGame}
          onLeaveRoom={leaveRoom}
          onAddBot={addBot}
          onRemoveBot={removeBot}
          onChangePlayerColor={changePlayerColor}
          onChangePlayerEmoji={changePlayerEmoji}
          onRandomizePlayerColor={randomizePlayerColor}
          onRandomizePlayerEmoji={randomizePlayerEmoji}
          onRandomizeAll={randomizeAll}
          availableColors={availableColors}
          availableEmojis={availableEmojis}
          countdown={countdown}
          socket={socket}
          chatMessages={chatMessages}
          isFirstPlayer={firstPlayer === name}
          botPersonalities={botPersonalities}
          onChangeBotPersonality={changeBotPersonality}
          gameOptions={gameOptions}
          onUpdateGameOption={updateGameOption}
        />
      )}
      <MessageOverlay snackbars={snackbars} handleSnackbarClose={handleSnackbarClose} />
    </div>
  );
};

export default GamePage;
